import React, { useState, useEffect, useRef } from 'react'
import { Row, Col, Container, Modal, Form, Button, Badge } from 'react-bootstrap'
import Card from '../../../components/Card'
import { InView } from "react-intersection-observer";
import { useNavigate } from 'react-router-dom'
import { apiHandler } from '../../../api/apihandler';
import {
   TEACHER_STUDENT_LEAVE_APPLICATIONS_API,
   LEAVE_APPLICATION_APPROVE_API,
   LEAVE_APPLICATION_REJECT_API
} from "../../../api/constants";
import LoaderModal from '../../../components/loaderModal';
import Swal from 'sweetalert2'
import Moment from 'moment';
import SignatureCanvas from 'react-signature-canvas';
import { handleImageUpload } from '../../../api/uploads';

// image
import loader from '../../../assets/images/page-img/page-load-loader.gif'
import NoDataFound from '../../../components/noDataFound';

const TeacherLeaveApplications = () => {

   const navigate = useNavigate();
   const [leaveApplicationList, setLeaveApplicationList] = useState([]);
   const [page, setPage] = useState(1);
   const [maxPage, setMaxPage] = useState(1);
   const [showLoader, setShowLoader] = useState(false);
   const [showMoreDataLoader, setShowMoreDataLoader] = useState(false);
   const [statusFilter, setStatusFilter] = useState('');
   const [expandedReasons, setExpandedReasons] = useState({});

   const [showApproveModal, setShowApproveModal] = useState(false);
   const [showRejectModal, setShowRejectModal] = useState(false);
   const [activeLeaveId, setActiveLeaveId] = useState(null);
   const [adminComments, setAdminComments] = useState('');
   const [isSubmittingDecision, setIsSubmittingDecision] = useState(false);

   const signaturePadRef = useRef(null);

   const [isFirst, setIsFirst] = useState(true);
   useEffect(() => {
      window.scrollTo({
         top: 0,
         behavior: "smooth",
      });
      setIsFirst(false)
   }, [isFirst]);

   useEffect(() => {
      if (localStorage.getItem("uzoneAuthToken")) {
         setLeaveApplicationList([])
         getLeaveApplicationList(1)
      } else {
         navigate('/')
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [statusFilter]);

   const loadMore = (inView) => {
      if (inView) {
         if (page < maxPage) {
            getLeaveApplicationList(page + 1)
            setPage(page + 1)
         }
      }
   };

   async function getLeaveApplicationList(pageVal) {
      if (pageVal === 1) {
         setShowLoader(true)
      } else {
         setShowMoreDataLoader(true)
      }
      try {
         const res = await apiHandler(TEACHER_STUDENT_LEAVE_APPLICATIONS_API, "POST", {
            page: pageVal,
            limit: 10,
            status: statusFilter
         })
         if (res.data?.response?.status === 200) {
            setMaxPage(res?.data?.data?.data?.meta?.last_page)
            if (res?.data?.data?.data?.meta?.total > 0) {
               if (pageVal === 1) {
                  setLeaveApplicationList(res?.data?.data?.data?.data)
               } else {
                  setLeaveApplicationList(leaveApplicationList.concat(res?.data?.data?.data?.data))
               }
            } else if (pageVal === 1) {
               setLeaveApplicationList([])
            }
         } else {
            Swal.fire({
               title: 'Error',
               text: res?.response?.data?.data?.message || 'Failed to load leave applications',
               icon: 'error',
               confirmButtonText: 'Okay'
            })
         }
      } catch (error) {
         console.error(error);
         Swal.fire({
            title: 'Error',
            text: 'Something went wrong. Please try again.',
            icon: 'error',
            confirmButtonText: 'Okay'
         })
      }
      finally {
         setShowMoreDataLoader(false)
         setShowLoader(false)
      }
   }

   const getStatusBadge = (status) => {
      switch (status) {
         case 1:
            return <Badge bg="primary">Approved</Badge>;
         case 2:
            return <Badge bg="danger">Rejected</Badge>;
         case 0:
            return <Badge bg="warning" text="dark">Pending</Badge>;
         default:
            return <Badge bg="secondary">Unknown</Badge>;
      }
   };

   const toggleReadMore = (id) => {
      setExpandedReasons(prev => ({
         ...prev,
         [id]: !prev[id]
      }));
   };

   const handleOpenApproveModal = (leaveId) => {
      setActiveLeaveId(leaveId);
      setAdminComments('');
      setShowApproveModal(true);
   };

   const handleOpenRejectModal = (leaveId) => {
      setActiveLeaveId(leaveId);
      setAdminComments('');
      setShowRejectModal(true);
   };

   const handleCloseApproveModal = () => {
      setShowApproveModal(false);
      setActiveLeaveId(null);
      if (signaturePadRef.current) {
         signaturePadRef.current.clear();
      }
   };

   const handleCloseRejectModal = () => {
      setShowRejectModal(false);
      setActiveLeaveId(null);
   };

   const handleClearSignature = () => {
      if (signaturePadRef.current) {
         signaturePadRef.current.clear();
      }
   };

   async function handleApprove() {
      if (!signaturePadRef.current || signaturePadRef.current.isEmpty()) {
         Swal.fire({
            title: 'Warning',
            text: 'Please provide your signature before approving.',
            icon: 'warning',
            confirmButtonText: 'Okay'
         })
         return;
      }

      setIsSubmittingDecision(true)
      try {
         const signatureDataURL = signaturePadRef.current.toDataURL('image/png');
         const signatureBlob = await fetch(signatureDataURL).then(res => res.blob());
         const signatureFile = new File([signatureBlob], `admin_signature_${Date.now()}.png`, { type: 'image/png' });
         const signatureUrl = await handleImageUpload(signatureFile, 'signatures/', 'image/png');

         const res = await apiHandler(LEAVE_APPLICATION_APPROVE_API, "POST", {
            id: activeLeaveId,
            admin_comments: adminComments.trim(),
            admin_signature: signatureUrl
         })
         if (res.data?.response?.status === 200) {
            Swal.fire({
               title: 'Success',
               text: res?.data?.data?.message || 'Leave application approved successfully',
               icon: 'success',
               confirmButtonText: 'Okay'
            })
            handleCloseApproveModal();
            setLeaveApplicationList([])
            getLeaveApplicationList(1)
         } else {
            Swal.fire({
               title: 'Error',
               text: res?.response?.data?.data?.errors?.[0] || res?.response?.data?.data?.message || 'Failed to approve leave application',
               icon: 'error',
               confirmButtonText: 'Okay'
            })
         }
      } catch (error) {
         console.error(error);
         Swal.fire({
            title: 'Error',
            text: 'Something went wrong. Please try again.',
            icon: 'error',
            confirmButtonText: 'Okay'
         })
      }
      finally {
         setIsSubmittingDecision(false)
      }
   }

   async function handleReject() {
      if (!adminComments || adminComments.trim() === '') {
         Swal.fire({
            title: 'Warning',
            text: 'Please provide a reason for rejecting this application.',
            icon: 'warning',
            confirmButtonText: 'Okay'
         })
         return;
      }

      setIsSubmittingDecision(true)
      try {
         const res = await apiHandler(LEAVE_APPLICATION_REJECT_API, "POST", {
            id: activeLeaveId,
            admin_comments: adminComments.trim()
         })
         if (res.data?.response?.status === 200) {
            Swal.fire({
               title: 'Success',
               text: res?.data?.data?.message || 'Leave application rejected successfully',
               icon: 'success',
               confirmButtonText: 'Okay'
            })
            handleCloseRejectModal();
            setLeaveApplicationList([])
            getLeaveApplicationList(1)
         } else {
            Swal.fire({
               title: 'Error',
               text: res?.response?.data?.data?.errors?.[0] || res?.response?.data?.data?.message || 'Failed to reject leave application',
               icon: 'error',
               confirmButtonText: 'Okay'
            })
         }
      } catch (error) {
         console.error(error);
         Swal.fire({
            title: 'Error',
            text: 'Something went wrong. Please try again.',
            icon: 'error',
            confirmButtonText: 'Okay'
         })
      }
      finally {
         setIsSubmittingDecision(false)
      }
   }

   const pendingCount = leaveApplicationList?.filter(leave => leave.status === 0).length || 0;
   const approvedCount = leaveApplicationList?.filter(leave => leave.status === 1).length || 0;
   const rejectedCount = leaveApplicationList?.filter(leave => leave.status === 2).length || 0;

   return (
      <>
         <LoaderModal showLoader={showLoader} />
         <div id='content-page' className='content-page scroller'>
            <Container>
               <Row>
                  <Col lg={12}>
                     <Card>
                        <Card.Header className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                           <div className="header-title">
                              <h4 className="card-title">Student Leave Applications</h4>
                           </div>
                           <Form.Select
                              style={{ maxWidth: '200px' }}
                              value={statusFilter}
                              onChange={(e) => setStatusFilter(e.target.value)}
                           >
                              <option value="">All Status</option>
                              <option value="0">Pending</option>
                              <option value="1">Approved</option>
                              <option value="2">Rejected</option>
                           </Form.Select>
                        </Card.Header>
                        <Card.Body>
                           <Row className="g-3 mb-4">
                              <Col xs={6} md={3}>
                                 <div className="text-center p-3 rounded bg-soft-warning">
                                    <h3 className="mb-0 text-warning fw-bold">{pendingCount}</h3>
                                    <p className="mb-0 text-muted small">Pending</p>
                                 </div>
                              </Col>
                              <Col xs={6} md={3}>
                                 <div className="text-center p-3 rounded bg-soft-primary">
                                    <h3 className="mb-0 text-primary fw-bold">{approvedCount}</h3>
                                    <p className="mb-0 text-muted small">Approved</p>
                                 </div>
                              </Col>
                              <Col xs={6} md={3}>
                                 <div className="text-center p-3 rounded bg-soft-danger">
                                    <h3 className="mb-0 text-danger fw-bold">{rejectedCount}</h3>
                                    <p className="mb-0 text-muted small">Rejected</p>
                                 </div>
                              </Col>
                              <Col xs={6} md={3}>
                                 <div className="text-center p-3 rounded bg-light">
                                    <h3 className="mb-0 fw-bold">{leaveApplicationList?.length || 0}</h3>
                                    <p className="mb-0 text-muted small">Total</p>
                                 </div>
                              </Col>
                           </Row>

                           {leaveApplicationList?.length > 0 ? leaveApplicationList.map((elm) => (
                              <div key={elm?.id} className="border rounded p-3 mb-3 bg-white shadow-sm">
                                 <div className="d-flex justify-content-between align-items-center mb-3 pb-2 border-bottom flex-wrap gap-2">
                                    <div className="d-flex align-items-center gap-2">
                                       <div className={`d-flex align-items-center justify-content-center rounded-circle ${elm?.status === 1 ? 'bg-soft-primary' : elm?.status === 2 ? 'bg-soft-danger' : 'bg-soft-warning'}`}
                                          style={{ width: '45px', height: '45px' }}>
                                          <i className={`ri-${elm?.leave_type === 'sick' ? 'heart-pulse' : elm?.leave_type === 'vacation' ? 'flight-takeoff' : elm?.leave_type === 'emergency' ? 'alarm-warning' : elm?.leave_type === 'family' ? 'parent' : 'calendar'}-line ${elm?.status === 1 ? 'text-primary' : elm?.status === 2 ? 'text-danger' : 'text-warning'}`}
                                             style={{ fontSize: '24px' }}></i>
                                       </div>
                                       <div>
                                          <h5 className="mb-0 fw-bold text-primary">
                                             {elm?.student?.first_name} {elm?.student?.last_name}
                                          </h5>
                                          <small className="text-muted text-capitalize">{elm?.leave_type} Leave</small>
                                       </div>
                                    </div>
                                    <div className="d-flex align-items-center gap-2">
                                       {getStatusBadge(elm?.status)}
                                       {elm?.status === 0 && (
                                          <>
                                             <Button variant="success" size="sm" onClick={() => handleOpenApproveModal(elm?.id)}>
                                                <i className="ri-check-line"></i> Approve
                                             </Button>
                                             <Button variant="outline-danger" size="sm" onClick={() => handleOpenRejectModal(elm?.id)}>
                                                <i className="ri-close-line"></i> Reject
                                             </Button>
                                          </>
                                       )}
                                    </div>
                                 </div>

                                 <div className="mb-3">
                                    <div className="ms-1 d-flex flex-wrap align-items-center gap-2">
                                       <span className="badge bg-light text-dark px-3 py-2">
                                          {Moment(elm?.from_date).format('MMM D, YYYY')}
                                       </span>
                                       <i className="ri-arrow-right-line text-muted"></i>
                                       <span className="badge bg-light text-dark px-3 py-2">
                                          {Moment(elm?.to_date).format('MMM D, YYYY')}
                                       </span>
                                       <span className="badge bg-primary text-white">
                                          {elm?.total_days} {elm?.total_days === 1 ? 'Day' : 'Days'}
                                       </span>
                                    </div>
                                 </div>

                                 <div className="mb-2 ms-1">
                                    <p className="mb-1 text-muted" style={{ lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                                       {elm?.reason?.length > 150 && !expandedReasons[elm?.id]
                                          ? `${elm?.reason.substring(0, 150)}...`
                                          : elm?.reason}
                                    </p>
                                    {elm?.reason?.length > 150 && (
                                       <Button
                                          variant="link"
                                          size="sm"
                                          className="p-0 text-decoration-none text-primary"
                                          onClick={() => toggleReadMore(elm?.id)}>
                                          {expandedReasons[elm?.id] ? 'Show Less' : 'Read More'}
                                       </Button>
                                    )}
                                 </div>

                                 {elm?.parent_signature && (
                                    <div className="border rounded p-2 mb-2" style={{ backgroundColor: '#f8f9fa', maxWidth: '250px' }}>
                                       <small className="fw-bold d-block mb-1">Parent Signature</small>
                                       <div style={{ height: '80px' }}>
                                          <img src={elm?.parent_signature} alt="Parent Signature" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                       </div>
                                    </div>
                                 )}

                                 {(elm?.admin_comments || elm?.admin_signature) && (
                                    <div className="mt-3 p-3 rounded bg-soft-primary" style={{ borderLeft: '4px solid var(--bs-primary)' }}>
                                       <strong className="text-primary d-block mb-2">Your Response</strong>
                                       {elm?.admin_signature && (
                                          <div className="border rounded p-2 bg-white mb-2" style={{ height: '80px', maxWidth: '250px' }}>
                                             <img src={elm?.admin_signature} alt="Signature" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                                          </div>
                                       )}
                                       {elm?.admin_comments && (
                                          <p className="mb-0 p-2 bg-white rounded border">{elm?.admin_comments}</p>
                                       )}
                                    </div>
                                 )}

                                 <div className="mt-3 pt-2 border-top">
                                    <small className="text-muted">
                                       Submitted on {Moment(elm?.created_at).format('MMM DD, YYYY [at] hh:mm A')}
                                    </small>
                                 </div>
                              </div>
                           )) : (
                              <div className="text-center py-5">
                                 <NoDataFound />
                              </div>
                           )}
                           {
                              showMoreDataLoader ?
                                 <div className="text-center py-3">
                                    <img src={loader} alt="loader" style={{ height: "80px" }} />
                                 </div> : null
                           }
                           <InView as="div" onChange={(inView, entry) => loadMore(inView)} />
                        </Card.Body>
                     </Card>
                  </Col>
               </Row>
            </Container>
         </div>

         {/* Approve Modal */}
         <Modal show={showApproveModal} onHide={handleCloseApproveModal} centered>
            <Modal.Header closeButton>
               <Modal.Title style={{ fontSize: '16px', fontWeight: '600' }}>
                  <i className="ri-checkbox-circle-line text-success me-2"></i>
                  Approve Leave Application
               </Modal.Title>
            </Modal.Header>
            <Modal.Body>
               <Form.Group className="mb-3">
                  <Form.Label>Comments (Optional)</Form.Label>
                  <Form.Control
                     as="textarea"
                     rows={3}
                     value={adminComments}
                     onChange={(e) => setAdminComments(e.target.value)}
                     placeholder="Add any comments regarding the approval..."
                  />
               </Form.Group>
               <Form.Group className="mb-3">
                  <Form.Label>Your Signature <span className="text-danger">*</span></Form.Label>
                  <div className="border rounded p-2 bg-white" style={{ width: '100%', height: '200px' }}>
                     <SignatureCanvas
                        ref={signaturePadRef}
                        canvasProps={{
                           className: 'signature-canvas',
                           style: { width: '100%', height: '100%' }
                        }}
                     />
                  </div>
                  <Button variant="outline-secondary" size="sm" className="mt-2" onClick={handleClearSignature} type="button">
                     Clear Signature
                  </Button>
               </Form.Group>
            </Modal.Body>
            <Modal.Footer>
               <Button variant="secondary" onClick={handleCloseApproveModal} disabled={isSubmittingDecision}>Cancel</Button>
               <Button variant="success" onClick={handleApprove} disabled={isSubmittingDecision}>
                  {isSubmittingDecision ? 'Approving...' : 'Approve Application'}
               </Button>
            </Modal.Footer>
         </Modal>

         {/* Reject Modal */}
         <Modal show={showRejectModal} onHide={handleCloseRejectModal} centered>
            <Modal.Header closeButton>
               <Modal.Title style={{ fontSize: '16px', fontWeight: '600' }}>
                  <i className="ri-close-circle-line text-danger me-2"></i>
                  Reject Leave Application
               </Modal.Title>
            </Modal.Header>
            <Modal.Body>
               <div className="alert alert-warning">
                  This action cannot be undone. Please provide a reason for rejection.
               </div>
               <Form.Group className="mb-3">
                  <Form.Label>Reason for Rejection <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                     as="textarea"
                     rows={3}
                     value={adminComments}
                     onChange={(e) => setAdminComments(e.target.value)}
                     placeholder="Please provide the reason for rejecting this application..."
                     required
                  />
               </Form.Group>
            </Modal.Body>
            <Modal.Footer>
               <Button variant="secondary" onClick={handleCloseRejectModal} disabled={isSubmittingDecision}>Cancel</Button>
               <Button variant="danger" onClick={handleReject} disabled={isSubmittingDecision}>
                  {isSubmittingDecision ? 'Rejecting...' : 'Reject Application'}
               </Button>
            </Modal.Footer>
         </Modal>
      </>
   )
}

export default TeacherLeaveApplications;
